import { DateFormatter, isNotUndefined } from 'infrastructure/functions';

import type { TUniqueDaysType } from '../templates/common';
import type {
  TFollowUpAndEscalationOptional,
  TFreeTextReadingData,
  TTwoWaySyncDataOptional,
} from '../types';

type TType =
  | 'systolic'
  | 'diastolic'
  | 'pulse'
  | 'SpO2'
  | 'blood glucose'
  | 'weight';

export const genThresholdText = (
  type: TType,
  avgValue?: number,
  avg30Value?: string,
  avg90Value?: string,
) => {
  const measurement = getMeasurement(type);
  let text = '';

  if (isNotUndefined(avgValue)) {
    // TODO after {type} add (date range of readings presented according to weekly alerting). e.g. '{type} for Jan 1–Jan 7'
    text += `Patient’s average ${type} was ${avgValue} ${measurement}. `;
  }
  if (avg30Value && avg90Value) {
    text += `This is ${avg30Value} from the patient’s 30 day average and ${avg90Value} from the patient’s 90 day average. `;
    return text;
  }
  if (avg30Value) {
    text += `This is ${avg30Value} from the patient’s 30 day average. `;
  }
  if (avg90Value) {
    text += `This is ${avg90Value} from the patient’s 90 day average. `;
  }
  return text;
};

const getMeasurement = (type: TType) => {
  switch (type) {
    case 'SpO2':
      return '%';

    case 'systolic':
    case 'diastolic':
      return 'mmHg';

    case 'pulse':
      return 'bpm';

    case 'blood glucose':
      return 'mg/dL';

    case 'weight':
      return 'lbs';

    default:
      return '';
  }
};

export const readingInTargetRangeText = (
  value: boolean | undefined,
  comment: string | undefined,
) => {
  let text = '';
  if (isNotUndefined(value)) {
    text += `The patient’s readings this week ${
      value ? 'were' : 'were not'
    } within target range. `;
    if (comment?.trim()) text += `${comment}. `;
  } else if (comment?.trim()) {
    text += `${comment}. `;
  }

  return text;
};

export const waitingDeviceReplacement = (
  value: boolean | undefined,
  list: string[] | undefined,
) => {
  let text = '';
  if (isNotUndefined(value)) {
    if (value) {
      const items = list?.join(', ');
      if (items?.length)
        text += `Patient is awaiting the following: ${items}. `;
    } else {
      text +=
        'Patient is not awaiting any device replacements, troubleshooting or supplies. ';
    }
  }
  return text;
};

interface IMaReviewedReadings {
  template: 'MA' | 'RN';
  type:
    | 'NIBP'
    | 'glucometer'
    | 'oximeter'
    | 'weight'
    | 'blood glucose'
    | 'SpO2';
  atRiskNumber: number | undefined;
  thresholds: (
    | {
        name: TType;
        lowValue: number | undefined;
        highValue: number | undefined;
      }
    | {
        name: 'trending';
        value: string | undefined;
      }
    | {
        name: 'weight-avg';
        value: number | undefined;
      }
  )[];
}

export const reviewedReadings = (data: IMaReviewedReadings) => {
  let text = '';
  const { type, atRiskNumber, thresholds, template } = data;

  if (atRiskNumber) {
    text +=
      template === 'RN'
        ? `Nurse assessed patient's ${type} readings. `
        : `MA reviewed ${atRiskNumber} ${type} readings. `;
  }

  thresholds.forEach((threshold) => {
    if (threshold.name === 'weight-avg') {
      if (threshold.value)
        text += `Average weight this week is ${threshold.value} lbs. `;
    } else if (threshold.name === 'trending') {
      if (threshold.value) text += `Readings are trending ${threshold.value}. `;
    } else if (threshold.lowValue && threshold.highValue) {
      const measurement = getMeasurement(threshold.name);
      text += `Patient’s ${threshold.name} range is ${threshold.lowValue} ${measurement} to ${threshold.highValue} ${measurement}. `;
    }
  });
  return text;
};

export const compareToLastReview = (
  lastReview: string | undefined,
  lastReviewTrend: string | undefined,
) => {
  const comparedToLastReviewText =
    'Compared to the last review, total number of at-risk readings have';
  const comparedToLastReviewTrendText = 'he at-risk trend is';

  let text = '';
  if (isNotUndefined(lastReview) && isNotUndefined(lastReviewTrend)) {
    text += `${comparedToLastReviewText} ${lastReview} and t${comparedToLastReviewTrendText} ${lastReviewTrend}. `;
  } else if (isNotUndefined(lastReview)) {
    text += `${comparedToLastReviewText} ${lastReview}. `;
  } else if (isNotUndefined(lastReviewTrend)) {
    text += `T${comparedToLastReviewTrendText} ${lastReviewTrend}. `;
  }

  return text;
};

export const patientAcknowledgement = (value: string | undefined) => {
  let text = '';

  if (isNotUndefined(value)) {
    text += 'Patient ';
    if (value === 'yes') text += 'was';
    if (value === 'no') text += 'was not';
    if (value === 'partially') text += 'was partially';
    text += ' able to verbalized understanding of education provided. ';
  }

  return text;
};

export const patientAgreeability = (value: string | undefined) => {
  let text = '';

  if (isNotUndefined(value)) {
    text += 'Patient ';
    if (value === 'yes') text += 'was';
    if (value === 'no') text += 'was not';
    text += ' agreeable to the nursing recommendation. ';
  }

  return text;
};

export const uniqueDaysText = (uniqueDays: number) =>
  `Patient has had ${uniqueDays} unique reading day${
    uniqueDays > 1 ? 's' : ''
  } this qualified period. `;

export const uniqueDaysTextWithType = (
  type: TUniqueDaysType,
  uniqueDays: number,
) =>
  `Patient took ${type} readings ${uniqueDays} ${
    uniqueDays > 1 ? 'days' : 'day'
  } of the last 7 days. `;

export const croOrDncText = (
  data: Omit<TTwoWaySyncDataOptional, 'uniqueDays'>,
) => {
  let text = '';

  if (isNotUndefined(data.completedPhoneCall)) {
    text += `Patient ${
      data.completedPhoneCall ? 'does' : 'does not'
    } require a monthly phone call.`;
    text += '\n';
  }

  if (data.attemptedPhoneCall === 'yes')
    text += `Monthly phone call completed. `;
  if (data.attemptedPhoneCall === 'no-reached')
    text += `Monthly phone call attempted, unable to reach patient. `;
  if (data.attemptedPhoneCall === 'no-reason')
    text += `Monthly phone call not attempted due to `;

  if (data.attemptedPhoneCallComment?.trim())
    text += `${data.attemptedPhoneCallComment}. `;

  return text;
};

export const additionalFollowUpText = (
  data: Pick<
    TFollowUpAndEscalationOptional,
    'additionalFollowUp' | 'additionalFollowUpComment'
  >,
) => {
  let text = '';

  if (isNotUndefined(data.additionalFollowUp)) {
    text += `Additional follow up ${
      data.additionalFollowUp ? 'is' : 'is not'
    } needed at this time. `;
  }

  if (data.additionalFollowUpComment?.trim()) {
    text += `${data.additionalFollowUpComment}. `;
  }

  return text;
};

export const needEscalateText = (
  data: Pick<
    TFollowUpAndEscalationOptional,
    'needEscalate' | 'needEscalateComment'
  >,
) => {
  let text = '';

  if (data.needEscalate === true)
    text += 'Case escalated to nursing team per protocol. ';
  if (data.needEscalateComment?.trim()) {
    text += `${data.needEscalateComment}. `;
  }

  return text;
};

export const freeTextBlurb = (data: TFreeTextReadingData) => {
  return `${Object.values(data)[0]}`;
};

type TAssessment = {
  title: string;
  name: string;
};

export const getAssessments = (
  arr: TAssessment[],
  data: Record<string, any>,
) => {
  const assessmentsCount = arr.length;
  const assessmentNames = arr.map((i) => i.name);
  const assessmentValues = Object.entries(data).reduce<
    { name: string; value: boolean }[]
  >((acc, [key, value]) => {
    if (assessmentNames.includes(key) && typeof value === 'boolean')
      acc.push({ name: key, value });

    return acc;
  }, []);

  const assessmentTrueNames = assessmentValues
    .filter((i) => i.value)
    .map((i) => i.name)
    .filter((i) => assessmentNames.includes(i));
  const assessmentFalseNames = assessmentValues
    .filter((i) => !i.value)
    .map((i) => i.name)
    .filter((i) => assessmentNames.includes(i));
  const isAllAssessmentsFalse =
    assessmentFalseNames.length === assessmentsCount;
  const assessmentTrueTitles = arr
    .filter((i) => assessmentTrueNames.includes(i.name))
    .map((i) => i.title);
  const assessmentFalseTitles = arr
    .filter((i) => assessmentFalseNames.includes(i.name))
    .map((i) => i.title);

  return {
    isAllAssessmentsFalse,
    assessmentTrueTitles,
    assessmentFalseTitles,
    assessmentTrueNames,
    assessmentFalseNames,
  };
};

type TData = {
  escalateToProviderReason?: string;
  notifyPharmacyReason?: string;
  scheduleFollowUpCallValue?: number;
};

export const getDecisionMakingNextSteps = (
  arr: string[] | undefined,
  data: TData,
) => {
  return arr
    ?.map((i) => {
      let result = i;

      if (data?.escalateToProviderReason) {
        result = result.replace(
          '<escalateToProviderReason>',
          data.escalateToProviderReason,
        );
      }

      if (data?.notifyPharmacyReason) {
        result = result.replace(
          '<notifyPharmacyReason>',
          data.notifyPharmacyReason,
        );
      }

      if (data?.scheduleFollowUpCallValue !== undefined) {
        result = result
          .replace('<value>', String(data.scheduleFollowUpCallValue))
          .replace(
            'days/weeks',
            data.scheduleFollowUpCallValue === 1 ? 'day/week' : 'days/weeks',
          );
      }

      return result;
    })
    .filter(
      (i) =>
        !i.includes('<escalateToProviderReason>') &&
        !i.includes('<notifyPharmacyReason>') &&
        !i.includes('<value>'),
    );
};

export const maTaskDelegated = (
  value: string | undefined,
  arr: string[] | undefined,
  arrComment: string | undefined,
) => {
  let text = '';
  if (isNotUndefined(value)) {
    const maTasksDelegatedList = arr?.join(', ');
    if (
      value === 'yes' &&
      (maTasksDelegatedList?.length || arrComment?.trim())
    ) {
      text +=
        'Nurse identified patient for medical assistant follow up to provide resources to address the following: ';
      if (maTasksDelegatedList?.length) {
        text += `${maTasksDelegatedList}. `;
      }
      if (arrComment?.trim()) {
        text += `${arrComment}. `;
      }
    }
    if (value === 'no') {
      text +=
        'Patient does not need medical assistant help with community resources at this time. ';
    }
  }
  return text;
};

export const followUpPlan = (
  value: string | undefined,
  date: Date | undefined,
) => {
  let text = '';
  if (isNotUndefined(value)) {
    const dateValue = DateFormatter({ date });
    if (value === 'yes' && dateValue) {
      text += `Nurse scheduled patient for follow up call on ${dateValue}. `;
    }
    if (value === 'no') {
      text += 'No additional follow-up needed. ';
    }
  }
  return text;
};
